import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { SUPPLIER_CHANNELS } from '@lensmira/domain'

import type { SupplierRow } from '../api/reads.js'
import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { supplierChannelLabel } from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Keeping the supplier list. §6.19 `SUP-001`, `SUP-003`; `later_todo.md` 53.
 *
 * **`insert into supplier` appeared once outside tests, in `tools/seed`.** So a shop could be
 * seeded with one supplier and never add a second, correct a lead time or set the channel its
 * orders go out by, and `RaisePurchaseOrder` told the shopkeeper to type the supplier's UUID.
 *
 * The routes exist now; this is the screen, and without it the chooser's own empty state
 * ("Add one under Suppliers") pointed at nothing.
 *
 * ---
 *
 * **The channel is the field this screen exists for.** `SUP-003`'s send reads
 * `supplier.transmission_channel` and refuses `NO_CHANNEL` when it is null, so an order to a
 * supplier without one cannot leave `draft`: §15.6 steps 2 and 3 stop there. A supplier with none
 * says so in the list, because "why will this order not send" is a question somebody asks at the
 * counter.
 *
 * **`whatsapp_image` is not offered.** `V1-SPEC.md` §2 puts WhatsApp out of v1 and `MSG-001`
 * makes v1 messaging email and in-app, so a channel the product cannot use is a channel this
 * screen must not set. `api` and `portal` are offered because Appendix E registers them and a shop
 * that orders through a supplier's portal is recording the truth; what none of the three does yet
 * is transmit anything, which `purchasing.service.ts` says in terms ("this records the channel and
 * moves the order to `sent`"). That is `later_todo.md` 53's remaining product call, not this
 * screen's to settle.
 *
 * **Nothing is deleted.** `purchase_order.supplier_id` references the row and every order ever
 * raised is attributed to it, so the list offers "stop using" and shows the ones already stopped
 * rather than hiding them: an order from last year still names them.
 */

/** Appendix E's `supplier_channel`, less the one `V1-SPEC.md` §2 puts out of v1. */
const OFFERED_CHANNELS = SUPPLIER_CHANNELS.filter((channel) => channel !== 'whatsapp_image')

export interface SuppliersProps {
  /** Appendix B's `Purchase order - raise`, which the create and change routes both take. */
  readonly canManage: boolean
  readonly loadSuppliers: () => Promise<readonly SupplierRow[]>
  /** Called once a write has gone through, so a chooser elsewhere can read the list again. */
  readonly onWritten?: () => void
  /**
   * Whether the caller holds the cost grant that `SupplierPrices` needs. `SUP-002`.
   *
   * The button is absent without it, not disabled: a button that leads to a denied wall is worse
   * than no button.
   */
  readonly canOpenPrices?: boolean
  readonly onOpenPrices?: (supplier: SupplierRow) => void
}

interface Draft {
  readonly name: string
  readonly code: string
  readonly countryCode: string
  readonly currency: string
  readonly channel: string
  readonly leadTimeDays: string
  readonly phone: string
  readonly email: string
}

const EMPTY: Draft = {
  name: '',
  code: '',
  countryCode: '',
  currency: '',
  channel: '',
  leadTimeDays: '',
  phone: '',
  email: '',
}

const draftOf = (supplier: SupplierRow): Draft => ({
  name: supplier.name,
  code: supplier.code,
  countryCode: supplier.countryCode,
  currency: supplier.currency,
  channel: supplier.transmissionChannel ?? '',
  leadTimeDays: supplier.leadTimeDays === null ? '' : String(supplier.leadTimeDays),
  phone: supplier.phone ?? '',
  email: supplier.email ?? '',
})

export function Suppliers({
  canManage,
  canOpenPrices,
  loadSuppliers,
  onOpenPrices,
  onWritten,
}: SuppliersProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [suppliers, setSuppliers] = useState<readonly SupplierRow[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(() => {
    loadSuppliers().then(
      (rows) => {
        setSuppliers(rows)
        setFailed(false)
      },
      () => {
        // Its own state, not an empty list: "this shop has no suppliers" and "the list could not
        // be read" are different sentences and only one is ever true at a time.
        setSuppliers(null)
        setFailed(true)
      },
    )
  }, [loadSuppliers])

  useEffect(() => {
    refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setEditing(null)
    setAdding(false)
    setDraft(EMPTY)
    refresh()
    onWritten?.()
  })

  /**
   * A write somebody discarded has to give the form back.
   *
   * `useWriteSettled` fires only for a write the server took, so one thrown away in
   * `BlockedWrites` would leave `writeId` set and the form frozen for good. `VoidSale.tsx` records
   * what that costs.
   */
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  const shaped = useCallback((): Record<string, unknown> | null => {
    for (const [value, key] of [
      [draft.name, 'suppliers.nameRequired'],
      [draft.code, 'suppliers.codeRequired'],
      [draft.countryCode, 'suppliers.countryRequired'],
      [draft.currency, 'suppliers.currencyRequired'],
    ] as const) {
      if (value.trim() === '') {
        setError(t(key))
        return null
      }
    }
    if (!/^[A-Za-z]{3}$/.test(draft.currency.trim())) {
      // `supplier_currency_is_iso` is a check constraint, and a violation arrives as a 500 that
      // the write queue retries forever.
      setError(t('suppliers.currencyShape'))
      return null
    }
    const lead = draft.leadTimeDays.trim()
    if (lead !== '' && !/^\d+$/.test(lead)) {
      setError(t('suppliers.leadTimeShape'))
      return null
    }
    setError(null)
    return {
      name: draft.name.trim(),
      code: draft.code.trim(),
      countryCode: draft.countryCode.trim(),
      currency: draft.currency.trim(),
      // Null clears it and absent leaves it alone, so an edit that empties the box has to say so.
      transmissionChannel: draft.channel === '' ? null : draft.channel,
      leadTimeDays: lead === '' ? null : Number(lead),
      phone: draft.phone.trim() === '' ? null : draft.phone.trim(),
      email: draft.email.trim() === '' ? null : draft.email.trim(),
    }
  }, [draft, t])

  const save = useCallback(
    async (supplier: SupplierRow | null) => {
      const body = shaped()
      if (body === null) return
      const write = (await enqueue(
        supplier === null
          ? {
              method: 'POST',
              path: '/suppliers',
              label: t('suppliers.queueAddLabel', { name: String(body['name']) }),
              body,
            }
          : {
              method: 'PATCH',
              path: `/suppliers/${encodeURIComponent(supplier.id)}`,
              label: t('suppliers.queueChangeLabel', { name: supplier.name }),
              body: { ...body, expectedVersion: supplier.version },
            },
      )) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, shaped, t],
  )

  const setActive = useCallback(
    async (supplier: SupplierRow, isActive: boolean) => {
      setError(null)
      const write = (await enqueue({
        method: 'PATCH',
        path: `/suppliers/${encodeURIComponent(supplier.id)}`,
        label: t(isActive ? 'suppliers.queueResumeLabel' : 'suppliers.queueStopLabel', {
          name: supplier.name,
        }),
        body: { isActive, expectedVersion: supplier.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  const field = (key: keyof Draft, label: string, type = 'text'): JSX.Element => (
    <label className="lm-field">
      <span className="lm-field__label">{t(label as Parameters<typeof t>[0])}</span>
      <input
        className="lm-input"
        type={type}
        dir="auto"
        autoComplete="off"
        value={draft[key]}
        onChange={(e) => {
          const next = e.currentTarget.value
          setDraft((was) => ({ ...was, [key]: next }))
        }}
      />
    </label>
  )

  const form = (supplier: SupplierRow | null): JSX.Element => (
    <div className="lm-supplier__form">
      {field('name', 'suppliers.name')}
      {field('code', 'suppliers.code')}
      {field('countryCode', 'suppliers.country')}
      {field('currency', 'suppliers.currency')}
      <label className="lm-field">
        <span className="lm-field__label">{t('suppliers.channel')}</span>
        <select
          className="lm-input"
          value={draft.channel}
          onChange={(e) => {
            const next = e.currentTarget.value
            setDraft((was) => ({ ...was, channel: next }))
          }}
        >
          <option value="">{t('suppliers.channelNone')}</option>
          {OFFERED_CHANNELS.map((channel) => (
            <option key={channel} value={channel}>
              {supplierChannelLabel(t, channel)}
            </option>
          ))}
        </select>
      </label>
      <p className="lm-hint">{t('suppliers.channelMeans')}</p>
      {field('leadTimeDays', 'suppliers.leadTime')}
      {field('phone', 'suppliers.phone')}
      {field('email', 'suppliers.email')}
      {error === null ? null : (
        <p className="lm-supplier__error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void save(supplier)
        }}
      >
        {t(supplier === null ? 'suppliers.add' : 'suppliers.save')}
      </button>
      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          setAdding(false)
          setEditing(null)
          setDraft(EMPTY)
          setError(null)
        }}
      >
        {t('suppliers.cancel')}
      </button>
    </div>
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.supplier'))

  return (
    <section className="lm-screen" aria-labelledby="suppliers-heading">
      <h1 id="suppliers-heading" className="lm-screen__heading">
        {t('suppliers.heading')}
      </h1>

      {/*
        §8 `ST-02`. The add button and form below stay outside the surface, so a shop with no
        suppliers, or a list that failed to read, still has the way to add one. The first-run
        sentence points at that button, which only someone who can manage suppliers has, so a
        read-only role gets a sentence that does not invite an action it cannot take.
      */}
      <DataSurface
        state={
          failed
            ? { kind: 'error', message: t('suppliers.loadFailed'), onRetry: refresh }
            : suppliers === null
              ? { kind: 'loading' }
              : suppliers.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('suppliers.what')}
        emptyMessage={canManage ? t('suppliers.none') : t('suppliers.noneReadOnly')}
      >
        <ul className="lm-supplier-list">
          {(suppliers ?? []).map((supplier) => (
            <li key={supplier.id} className="lm-supplier">
              <span className="lm-supplier__name" dir="auto">
                {supplier.name}
              </span>
              <span className="lm-supplier__meta" dir="ltr">
                {supplier.code}
              </span>
              <span className="lm-supplier__meta">
                {supplier.transmissionChannel === null
                  ? t('suppliers.noChannelWarning')
                  : supplierChannelLabel(t, supplier.transmissionChannel)}
                {supplier.isActive ? '' : ` · ${t('suppliers.stopped')}`}
              </span>
              {canOpenPrices === true && onOpenPrices !== undefined ? (
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    onOpenPrices(supplier)
                  }}
                >
                  {t('supplierPrices.heading', { name: supplier.name })}
                </button>
              ) : null}
              {canManage && editing === supplier.id ? (
                form(supplier)
              ) : canManage ? (
                <>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setAdding(false)
                      setEditing(supplier.id)
                      setDraft(draftOf(supplier))
                      setError(null)
                    }}
                  >
                    {t('suppliers.change', { name: supplier.name })}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={writeId !== null}
                    onClick={() => {
                      void setActive(supplier, !supplier.isActive)
                    }}
                  >
                    {t(supplier.isActive ? 'suppliers.stop' : 'suppliers.resume', {
                      name: supplier.name,
                    })}
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </DataSurface>

      {canManage ? (
        adding ? (
          form(null)
        ) : (
          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => {
              setEditing(null)
              setAdding(true)
              setDraft(EMPTY)
              setError(null)
            }}
          >
            {t('suppliers.addOne')}
          </button>
        )
      ) : null}

      {line === null ? null : <p className="lm-hint">{line}</p>}
    </section>
  )
}
